// src/pages/SubmitIncident.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainContent from '../components/MainContent';
import { useCreateIncident, useGetIncidentSuggestions } from '../api/queries';
import { Incident, CreateIncidentRequest, IncidentResponse } from '../types/incident';
import { APP_ROUTES } from '../routes';

// Helper function to convert IncidentResponse to Incident, can be moved to a util file
const convertToIncident = (response: IncidentResponse): Incident => {
    const validStatuses = ["open", "in_progress", "resolved", "closed"] as const;
    const status = validStatuses.includes(response.status as any)
        ? response.status as "open" | "in_progress" | "resolved" | "closed"
        : "open"; // fallback to default status

    return {
        ...response,
        status,
        updated_at: response.updated_at || response.created_at,
    };
};

export default function SubmitIncident() {
    const [incidentDescription, setIncidentDescription] = useState('');
    const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
    const [createdIncident, setCreatedIncident] = useState<Incident | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const navigate = useNavigate();

    const { mutate: createIncident, isPending: isSubmitting, error: createError } = useCreateIncident();
    
    const { data: suggestions, isLoading: analysisLoading, error: analysisQueryError } = useGetIncidentSuggestions(createdIncident?.id ?? '');

    const handleSubmit = () => {
        if (!incidentDescription.trim()) {
            setFormError('Please provide a description for the incident');
            return;
        }

        setFormError(null);
        setCreatedIncident(null);

        const incidentData: CreateIncidentRequest = {
            description: incidentDescription.trim(),
            priority,
        };
        
        createIncident(incidentData, {
            onSuccess: (response) => {
                setCreatedIncident(convertToIncident(response));
            }
        });
    };

    const handleCreateAnother = () => {
        setCreatedIncident(null);
        setFormError(null);
        setIncidentDescription('');
        setPriority('medium');
    };

    const submitError = createError ? createError.message : formError;
    const analysisError = analysisQueryError ? analysisQueryError.message : null;
    
    const incidentForDisplay: Incident | null = createdIncident 
        ? {
            ...createdIncident,
            analysis: suggestions && suggestions.length > 0 ? suggestions[0] : undefined,
          }
        : null;

    return (
        <div className="min-h-screen bg-gray-50">
            <MainContent
                incidentDescription={incidentDescription}
                onDescriptionChange={setIncidentDescription}
                onSubmit={handleSubmit}
                priority={priority}
                onPriorityChange={setPriority}
                isSubmitting={isSubmitting}
                submitError={submitError}
                createdIncident={incidentForDisplay}
                onCreateAnother={handleCreateAnother}
                analysisLoading={analysisLoading}
                analysisError={analysisError}
                onViewAllIncidents={() => navigate(APP_ROUTES.INCIDENT_LIST)}
            />
        </div>
    );
}
